import { Injectable, inject } from '@angular/core';
import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ShowToastErrorAction } from '@smatchy/core';
import { ConfigService } from '@smatchy/ionic-core';
import { IOSSettings, NativeSettings } from 'capacitor-native-settings';

@Injectable({
  providedIn: 'root',
})
export class CameraAdapter {
  private readonly config: ConfigService = inject(ConfigService);

  constructor(
    private readonly _store: Store,
    private readonly _translate: TranslateService,
    private readonly alertController: AlertController,
  ) {}

  /**
   * The picture the person chose, or nothing.
   *
   * `| undefined` because the body already answers nothing twice: when the
   * permission is refused it returns bare, and when the camera throws it shows
   * a toast and falls out of the try. The single caller,
   * `MediaAdapter.pickImageAndUpload`, already tests the result before using
   * it -- the signature was the only thing claiming a picture always comes
   * back.
   */
  async pickImage(): Promise<
    | {
        name: string;
        url: string;
      }
    | undefined
  > {
    if (!this.config.isBrowser) {
      try {
        if (await this.checkDeniedPermissions()) {
          return;
        }
        const galleryPhoto: Photo = await Camera.getPhoto({
          source: CameraSource.Photos,
          saveToGallery: false,
          resultType: CameraResultType.DataUrl,
          allowEditing: false,
          promptLabelHeader: this._translate.instant('CAMERA.HEADER'),
          promptLabelPhoto: this._translate.instant('CAMERA.FROM_PHOTO'),
          promptLabelCancel: this._translate.instant('GENERICS.LABELS.CANCEL'),
          promptLabelPicture: this._translate.instant('CAMERA.TAKE_PHOTO'),
        });
        if (galleryPhoto) {
          // `dataUrl` is optional on Capacitor's Photo: it is filled for the
          // DataUrl result type asked for above, and absent for the others. A
          // photo without one is no picture, so it joins the throw below.
          if (galleryPhoto.dataUrl) {
            return {
              name: 'Image.' + galleryPhoto.format, // No filename in galleryPhoto
              url: galleryPhoto.dataUrl,
            };
          }
        }
        throw new Error(this._translate.instant('CAMERA.NO_IMAGE_SELECTED'));
      } catch (e) {
        console.error(e);
        this._store.dispatch(
          new ShowToastErrorAction(
            // `e` is unknown in a catch, which is what it truly is: anything
            // can be thrown. Read the message only from something that has one.
            (e instanceof Error && e.message) || this._translate.instant('CAMERA.SAVE_FAILED'),
          ),
        );
      }
    } else {
      return new Promise((resolve) => {
        const inputFile = document.createElement('input');
        inputFile.type = 'file';

        // The filter has to be on the input before it opens: set after
        // click(), it came too late and the picker offered every file, a PDF
        // included (recette 2.2.11).
        inputFile.setAttribute('accept', 'image/png, image/jpeg');
        document.body.appendChild(inputFile);
        inputFile.click();
        inputFile.style.display = 'none';

        inputFile.addEventListener('change', (event: Event) => {
          const target = event.target as HTMLInputElement;
          // `files` is null on an input that never received one, and the list
          // can be empty; the `if` below already expected nothing to come back.
          const file = target.files?.[0];
          if (file) {
            const reader = new FileReader();

            reader.onload = function (event) {
              // `target` is null on a FileReader event that carries no result.
              const dataURL = event.target?.result;

              resolve({
                name: file.name,
                url: dataURL as string,
              });
            };
            // Read the content of the file as a data URL
            reader.readAsDataURL(file);
          }
          document.body.removeChild(inputFile);
        });
      });
    }
  }

  /**
   * Special case for iOS where the user has denied the permission
   * We open the settings to allow the user to enable the permission
   */
  protected async checkDeniedPermissions(): Promise<boolean> {
    const permissions = await Camera.checkPermissions();
    if (Capacitor.getPlatform() === 'ios' && permissions.photos === 'denied') {
      const alert = await this.alertController.create({
        header: this._translate.instant('CAMERA.PERMISSION_DENIED_HEADER'),
        message: this._translate.instant('CAMERA.PERMISSION_DENIED_MESSAGE'),
        buttons: [
          {
            text: this._translate.instant('CAMERA.OPEN_SETTINGS'),
            handler: () => {
              NativeSettings.openIOS({
                option: IOSSettings.App,
              });
            },
            htmlAttributes: {
              color: 'danger',
            },
          },
          {
            text: this._translate.instant('CAMERA.CANCEL'),
            role: 'cancel',
          },
        ],
      });
      await alert.present();
      return true;
    }
    return false;
  }
}
